import { Button, Drawer, Stack, Typography } from '@mui/material'
import { FC, useState } from 'react'
import { Control, FieldErrors, useFieldArray } from 'react-hook-form'
import { Fade, IconButton, Icons } from 'utoniq-ui'

import {
  Item,
  PreSaleLotteryDetailFragment,
} from '@/clients/utoniq-core/schema'
import { ThroughTicketTag } from '@/components/organisms'
import { useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'

import { ApplyPreSaleLotteryValue } from '../LotteryDetailForm'
import {
  QuantitySelectModal,
  QuantitySelectModalProps,
} from './QuantitySelectModal'
import { SkuUnitSelectModal } from './SkuUnitSelectModal'
import { TicketSelectModal } from './TicketSelectModal'

type TicketListCellProps = {
  items: Partial<Item>[]
  maxChoice?: number
  maxSelectItem?: number
  hasEmptyField: boolean
  setHasEmptyField: (value: boolean) => void
  control: Control<ApplyPreSaleLotteryValue>
  errors: FieldErrors<ApplyPreSaleLotteryValue>
  isBeforeReceiption: boolean
  hasValidField: boolean
  isAccessible: PreSaleLotteryDetailFragment['isAccessible']
  toggleMembershipModalOpen: () => void
}

const MAX_ADD_ITEM = 3
const MAX_SELECT_ITEM = 8

export const TicketListCell: Cell<TicketListCellProps> = ({
  items,
  maxChoice = MAX_ADD_ITEM,
  maxSelectItem = MAX_SELECT_ITEM,
  control,
  hasEmptyField,
  setHasEmptyField,
  isBeforeReceiption,
  hasValidField,
  isAccessible,
  toggleMembershipModalOpen,
}) => {
  const { t } = useTranslation('lotteryDetail')
  const [open, setOpen] = useState<boolean>(false)

  const [selectedIndex, setSelectedIndex] = useState<number>(0)
  const {
    fields: choiceFields,
    append: appendChoice,
    update: updateChoice,
    remove: removeChoice,
  } = useFieldArray({
    control,
    name: 'choices',
  })
  const {
    fields: purchaseCountChoiceFields,
    append: appendPurchaseCountChoice,
    update: updatePurchaseCountChoice,
    remove: removePurchaseCountChoice,
  } = useFieldArray({
    control,
    name: 'purchaseCountChoices',
  })

  return (
    <CellFrame sx={{ pt: 3, pb: 7 }}>
      <Typography
        variant="h6"
        sx={{ bgcolor: 'grey.50', px: 2, py: 1 }}
        color="text.secondary"
      >
        {t('selectTicketDetails', '申込内容の選択')}
      </Typography>
      {maxChoice > 1 ? (
        <Stack sx={{ py: 4 }}>
          <Typography component="p" variant="caption">
            {t('selectTicketDesc', { max: maxChoice })}
          </Typography>
        </Stack>
      ) : (
        <Stack height={8} />
      )}
      <Stack spacing={4}>
        {choiceFields.map(({ id, itemId, skuId }, index) => {
          const item = items?.find(({ id }) => id === itemId)
          const skuUnit = skuId
            ? item?.skuSettings?.units.find(sku => sku.id === skuId)
            : null
          return (
            <Stack sx={{ position: 'relative' }}>
              <Button
                key={id}
                variant="outlined"
                sx={{
                  p: 4,
                  borderRadius: 2,
                  alignItems: 'flex-start',
                  flexDirection: 'column',
                  pointerEvents: item ? 'none' : 'unset',
                }}
                onClick={() => {
                  // メンバーシップ限定の抽選でユーザーが対象外の場合、モーダルを表示
                  if (!isAccessible) {
                    toggleMembershipModalOpen()
                    return
                  }

                  setSelectedIndex(index)
                  setOpen(true)
                }}
              >
                {maxChoice > 1 && (
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    lineHeight="30px"
                  >
                    {t('preference', { number: index + 1 })}
                  </Typography>
                )}
                {item ? (
                  <Stack sx={{ mr: 7 }}>
                    <Typography variant="subtitle1" align="left">
                      {item.title}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {!!skuUnit?.unitName && (
                        <Typography variant="body2" align="left">
                          {skuUnit.unitName}
                        </Typography>
                      )}
                      {item.metadata?.ticket?.isThroughTicket && (
                        <ThroughTicketTag />
                      )}
                    </Stack>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      sx={{ pt: 1 }}
                    >
                      <Typography variant="subtitle1">
                        ¥{skuUnit?.price || item.formattedPrice}/
                      </Typography>
                      <Typography variant="subtitle1">
                        {purchaseCountChoiceFields[index]?.count}
                        {t('sheet', '枚')}
                      </Typography>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack direction="row" alignItems="center" sx={{ py: 1 }}>
                    <Typography variant="button" color="primary">
                      {t('selectTicket', 'チケットを選択する')}
                    </Typography>
                    <Icons.Next color="primary" />
                  </Stack>
                )}
              </Button>
              {item && (
                <IconButton.Close
                  size="small"
                  bgcolor="grey.50"
                  sx={{ position: 'absolute', zIndex: 2, right: 16, top: 16 }}
                  onClick={() => {
                    // チケット希望を取り消す処理。1つしかフィールドがない場合は、フィールドを削除せずに初期化する。
                    if (choiceFields.length === 1) {
                      updateChoice(index, { itemId: '' })
                      updatePurchaseCountChoice(index, { count: 1 })
                      setHasEmptyField(true)
                      return
                    }

                    removeChoice(index)
                    removePurchaseCountChoice(index)
                  }}
                />
              )}
            </Stack>
          )
        })}
      </Stack>
      {!hasEmptyField && maxChoice - choiceFields.length > 0 && hasValidField && (
        <Stack
          justifyContent="flex-end"
          flex={1}
          alignItems="flex-end"
          sx={{ pt: 4 }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              appendChoice({ itemId: '' })
              appendPurchaseCountChoice({ count: 1 })
              setHasEmptyField(true)
            }}
            startIcon={
              <Icons.Plus fontSize="medium" sx={{ color: 'grey.700' }} />
            }
          >
            {t('addSelect', { number: choiceFields.length + 1 })}
          </Button>
        </Stack>
      )}
      <TicketBottomSheet
        items={items}
        selectedIndex={selectedIndex}
        selectedItems={choiceFields}
        open={open}
        isBeforeReceiption={isBeforeReceiption}
        maxChoice={maxChoice}
        maxSelectItem={maxSelectItem}
        onClose={() => setOpen(false)}
        onSubmit={async ({ choiceId, choiceSkuId, purchaseCountChoice }) => {
          updateChoice(selectedIndex, { itemId: choiceId, skuId: choiceSkuId })
          updatePurchaseCountChoice(selectedIndex, {
            count: purchaseCountChoice,
          })
          setHasEmptyField(false)
        }}
      />
    </CellFrame>
  )
}

type TicketBottomSheetProps = {
  items: Partial<Item>[]
  selectedItems: ApplyPreSaleLotteryValue['choices']
  open: boolean
  maxChoice: number
  selectedIndex: number
  maxSelectItem: number
  onClose: () => void
  onSubmit: QuantitySelectModalProps['onSubmit']
  isBeforeReceiption: boolean
}
const TicketBottomSheet: FC<TicketBottomSheetProps> = ({
  items,
  selectedItems,
  open,
  maxChoice,
  selectedIndex,
  maxSelectItem,
  onClose,
  onSubmit,
  isBeforeReceiption,
}) => {
  const [step, setStep] = useState<number>(0)
  const [item, setItem] = useState<{
    item: Partial<Item> | undefined // アイテムデータ
    selectSkuId: string | undefined // SKUがある場合、選択したSKU ID
  } | null>(null)

  const handleOnClose = () => {
    onClose()
    setStep(0)
    setItem(null)
  }

  return (
    <Drawer open={open} onClose={handleOnClose}>
      <Stack height="75dvh" sx={{ bgcolor: 'background.default' }}>
        {step === 0 && (
          <Fade>
            <TicketSelectModal
              items={items}
              selectedItems={selectedItems}
              onClose={onClose}
              isBeforeReceiption={isBeforeReceiption}
              onSelectItem={item => {
                setItem({ item, selectSkuId: undefined })
                if (item?.skuSettings?.units?.length) {
                  return setStep(1)
                }
                setStep(2)
              }}
            />
          </Fade>
        )}
        {step === 1 && item?.item && (
          <Fade>
            <SkuUnitSelectModal
              item={item?.item}
              selectedItems={selectedItems}
              onClose={handleOnClose}
              onGoBack={() => {
                setStep(0)
                setItem(null)
              }}
              isBeforeReceiption={isBeforeReceiption}
              onSelectItem={skuId => {
                setItem({ item: item?.item, selectSkuId: skuId })
                setStep(2)
              }}
            />
          </Fade>
        )}
        {step === 2 && item?.item && (
          <Fade>
            <QuantitySelectModal
              item={item.item}
              skuId={item.selectSkuId}
              maxChoice={maxChoice}
              selectedIndex={selectedIndex}
              onGoBack={() => {
                if (item.item?.skuSettings?.units?.length) {
                  return setStep(1)
                }
                setStep(0)
              }}
              maxSelectItem={maxSelectItem}
              onSubmit={async values => {
                await onSubmit(values)
                onClose()
                setStep(0)
                setItem(null)
              }}
            />
          </Fade>
        )}
      </Stack>
    </Drawer>
  )
}
